// Finds evidence matches in the stored extracted text shown when the source PDF
// is unavailable, using the same normalisation as the PDF text-layer
// highlighter: case, whitespace, quote and dash differences are ignored, with a
// letters-and-digits-only retry when nothing matches.

export type TextRange = { start: number; end: number }

const normalizeChar = (ch: string) =>
  ch
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[‘’‚′‵`´]/g, "'")
    .replace(/[“”„″‶]/g, '"')
    .replace(/[–—]/g, '-')
    .toLowerCase()

// Each kept character of the text, normalised, with its index in the original
const buildCharMap = (text: string, keep: (ch: string) => boolean) => {
  const chars: string[] = []
  const rawIndexes: number[] = []
  for (let i = 0; i < text.length; i += 1) {
    for (const ch of normalizeChar(text[i])) {
      if (!keep(ch)) continue
      chars.push(ch)
      rawIndexes.push(i)
    }
  }
  return { compact: chars.join(''), rawIndexes }
}

const compactQuery = (query: string, keep: (ch: string) => boolean) =>
  Array.from(String(query || ''))
    .map(normalizeChar)
    .join('')
    .split('')
    .filter(keep)
    .join('')

const notWhitespace = (ch: string) => !/\s/.test(ch)
const isAlnum = (ch: string) => /[a-z0-9]/.test(ch)

const findRanges = (
  text: string,
  query: string,
  keep: (ch: string) => boolean
): TextRange[] => {
  const target = compactQuery(query, keep)
  if (!target) return []
  const { compact, rawIndexes } = buildCharMap(text, keep)
  const ranges: TextRange[] = []
  let cursor = 0
  while (cursor < compact.length) {
    const index = compact.indexOf(target, cursor)
    if (index === -1) break
    ranges.push({
      start: rawIndexes[index],
      end: rawIndexes[index + target.length - 1] + 1,
    })
    cursor = index + target.length
  }
  return ranges
}

const rangesForQuery = (text: string, query: string): TextRange[] => {
  const exact = findRanges(text, query, notWhitespace)
  if (exact.length > 0) return exact
  // Short alphanumeric-only queries would match too loosely
  return compactQuery(query, isAlnum).length >= 4
    ? findRanges(text, query, isAlnum)
    : []
}

const mergeRanges = (ranges: TextRange[]): TextRange[] => {
  const sorted = [...ranges].sort((a, b) => a.start - b.start)
  const merged: TextRange[] = []
  sorted.forEach((range) => {
    const last = merged[merged.length - 1]
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end)
    } else {
      merged.push({ ...range })
    }
  })
  return merged
}

const wordCount = (value: string) =>
  String(value || '')
    .split(/\s+/)
    .filter((word) => /[a-z0-9]/i.test(word)).length

// A fragment of a long search must be long enough to identify the passage,
// so short phrases such as "accordance with the" don't produce a false match.
const isDistinctiveEnough = (fragment: string, fullSearch: string) =>
  wordCount(fragment) >= Math.min(wordCount(fullSearch), 6) ||
  /\d{3,}/.test(fragment.replace(/[\s,.]/g, ''))

// With singleCandidate (one best query rather than a list of values), one
// match is highlighted: the first occurrence of the longest distinctive query
// that matches. queries[0] is the full search.
export const findFallbackTextMatches = (
  text: string,
  queries: string[],
  singleCandidate: boolean
): TextRange[] => {
  if (!text || queries.length === 0) return []
  if (singleCandidate) {
    const fullSearch = queries[0]
    const best = queries
      .filter((query) => isDistinctiveEnough(query, fullSearch))
      .map((query) => ({ query, ranges: rangesForQuery(text, query) }))
      .filter(({ ranges }) => ranges.length > 0)
      .sort((a, b) => wordCount(b.query) - wordCount(a.query))[0]
    return best ? [best.ranges[0]] : []
  }
  return mergeRanges(queries.flatMap((query) => rangesForQuery(text, query)))
}

export type TextSegment = { text: string; matchIndex: number | null }

export const splitTextByRanges = (
  text: string,
  ranges: TextRange[]
): TextSegment[] => {
  const segments: TextSegment[] = []
  let cursor = 0
  ranges.forEach((range, matchIndex) => {
    if (range.start > cursor) {
      segments.push({ text: text.slice(cursor, range.start), matchIndex: null })
    }
    segments.push({ text: text.slice(range.start, range.end), matchIndex })
    cursor = range.end
  })
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), matchIndex: null })
  }
  return segments
}
